import React, { useCallback, useState } from 'react';

import { RxMagicWand } from 'react-icons/rx';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';

import DynamicContentRenderer from './DynamicContentRenderer';
import GlobalCard from './GlobalCard';
import { useConnectionPointsData } from '../hooks/useConnectionPointsData';
import { getCategoryIcon } from '../utils/iconMapping';
import { useTheme } from 'hooks/useTheme';

type Props = {
  fromUserId: string;
  toUserId: string;
};

const AccordionConnectionPoints: React.FC<Props> = ({ fromUserId, toUserId }) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isDev = process.env.NODE_ENV === 'development';

  // Only fetch data when accordion is expanded (lazy loading)
  const { data, loading, error } = useConnectionPointsData(fromUserId, toUserId, expanded);

  const isOwnProfile = fromUserId === toUserId;

  if (isDev) console.log('[AccordionConnectionPoints] Data:', data);

  // Memoize getUppercaseWords function
  const getUppercaseWords = useCallback((text: string) => {
    const words = text
      .split(' ')
      .filter(word => word === word.toUpperCase())
      .map(word => {
        // Remove special characters but keep accented letters
        const cleanWord = word.replace(/[^a-zA-ZÀ-ÿ0-9]/g, '').toLowerCase();
        // Capitalize first letter
        return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1);
      });

    // Se não encontrou nenhuma palavra em maiúsculo, retorna o label original
    return words.length > 0 ? words.join(' ') : text;
  }, []);

  // Memoize mapDataToDynamicItems function
  const mapDataToDynamicItems = useCallback(
    (field: any) => {
      const items: Array<{
        id: string;
        label: string;
        value: string;
        subtitle?: string | null;
        variant?: 'default' | 'highlighted' | 'editable';
        showEditIcon?: boolean;
      }> = [];

      if (field.responses && Array.isArray(field.responses)) {
        field.responses.forEach((response: any, index: number) => {
          if (response && response.value != null) {
            items.push({
              id: `${field.uuid}_${index}`,
              label: getUppercaseWords(field.name),
              value: response.value,
              subtitle: field.field_type === 'tags' ? response.value : null,
              variant: response.is_common ? ('highlighted' as const) : ('default' as const),
              showEditIcon: response.is_common, // Ativa o wizard icon quando é comum
            });
          }
        });
      }

      return items;
    },
    [getUppercaseWords]
  );

  // Memoize getCategoryFromName function
  const getCategoryFromName = useCallback((categoryName: string): string => {
    const normalizedCategory = categoryName.toLowerCase().replace(/\s+/g, '_');

    // Mapeamento de nomes de categoria para categorias
    const categoryNameMapping: Record<string, string> = {
      perfil_pessoal_e_familiar: 'perfil_pessoal_familiar',
      preferências_pessoais: 'preferencias_pessoais',
      perfil_profissional_e_carreira: 'perfil_profissional_carreira',
      perfil_empresarial_e_de_negócios: 'perfil_empresarial_negocios',
      preferências_profissionais: 'preferencias_profissionais',
      'desafios,_propósito_e_legado': 'desafios_proposito_legado',
      localidade_chave: 'localidade_chave',
      desafios_e_projetos_da_sua_empresa_para_2025: 'desafios_empresa_2025',
      desafios_e_projetos_da_área_comercial: 'desafios_area_comercial',
      biografia: 'biografia',
    };

    return categoryNameMapping[normalizedCategory] || normalizedCategory;
  }, []);

  const commonCount = data?.total_common ?? 0;

  if (error) {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" fontWeight="bold">
            Pontos de Conexão
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="error">
            Erro ao carregar pontos de conexão: {error}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="h6" fontWeight="bold">
            Pontos de Conexão
          </Typography>
          {commonCount > 0 && !isOwnProfile && (
            <Chip
              label={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <span>Em comum:</span>
                  <RxMagicWand size={12} />
                  <span>{commonCount}</span>
                </Box>
              }
              size="small"
              sx={{
                backgroundColor: theme.primary,
                color: theme.background.primary,
                fontWeight: 'bold',
                fontSize: '0.75rem',
                marginRight: 1,
              }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">Carregando dados...</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {(() => {
                if (!data || !data.data || data.data.length === 0) {
                  return (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum dado encontrado para os pontos de conexão.
                      </Typography>
                    </Grid>
                  );
                }

                return data.data.map((category: any) => {
                  const fieldsWithResponses = category.fields.filter(
                    (field: any) => field.responses && field.responses.length > 0
                  );

                  if (fieldsWithResponses.length === 0) return null;

                  const categorySlug = getCategoryFromName(category.name);
                  const categoryIcon = getCategoryIcon(categorySlug);

                  return (
                    <Grid item xs={12} sm={6} md={6} lg={6} key={category.uuid}>
                      <GlobalCard icon={categoryIcon} title={category.name}>
                        {fieldsWithResponses.map((field: any) => {
                          const dynamicItems = mapDataToDynamicItems(field);

                          if (dynamicItems.length === 0) return null;

                          // Usa o slug do campo se disponível, senão usa o da categoria
                          const fieldCategorySlug = getCategoryFromName(category.name);

                          return (
                            <Box key={field.uuid} mt={1}>
                              <DynamicContentRenderer
                                category={field.slug ? field.slug : fieldCategorySlug}
                                title={getUppercaseWords(field.name)}
                                items={dynamicItems}
                                layout="grid"
                                columns={2}
                                fieldType={field.field_type}
                                slug={field.slug}
                              />
                            </Box>
                          );
                        })}
                      </GlobalCard>
                    </Grid>
                  );
                });
              })()}
            </Grid>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default React.memo(AccordionConnectionPoints, (prevProps, nextProps) => {
  return prevProps.fromUserId === nextProps.fromUserId && prevProps.toUserId === nextProps.toUserId;
});
